import React, { ChangeEvent, FC, useCallback } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { FeatureFlag } from 'app/api/services/site-configs/types';
import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { Checkbox } from 'app/design-system/components/Checkbox';
import { useTranslation } from 'app/lang';
import { useFeatureFlag } from 'app/lib/feature-flags';

import { ChangeStakeTypeAmount, changeStakeTypeAmount } from '../../../actions/stakeTypes';
import { AmountInputModifier, AmountValidationResult, SubmissionState } from '../../../constants';
import { RecordOfStakeTypeAmountType } from '../../../records';
import { selectHasEnoughMoney } from '../../../selectors';
import { selectTotalAmountActive } from '../../../selectors/entries';
import { selectAmountStakeType } from '../../../selectors/stakeTypes';
import {
  selectSingleAmountEntry,
  selectSingleAmountModifier,
  selectSingleAmountsValidation,
  selectSingleBetsInfo,
  selectSingleEntryIdsWithTheLargestMinLimit,
  selectSinglesMaxWinning,
} from '../../../selectors/stakeTypes/single';
import { selectSubmission } from '../../../selectors/submission';
import { getPresetsAndFreebetsBar } from '../../FooterBar';
import MinLimitError from '../../MinLimitError';
import NoMoneyLink from '../../NoMoneyLink';
import TextWithAmountBar from '../../TextWithAmountBar';
import TotalAmountInputToggle from '../../Toggle/TotalAmountInput';
import AmountInput from './AmountInput';

import dictionary from './Single.dictionary.json';

import styles from './SingleStakeType.module.scss';

// ---

type DispatchProp = {
  dispatch: ThunkDispatchType<ChangeStakeTypeAmount>;
};

type StoreProps = {
  betsSize: number;
  totalAmountActive: boolean;
  singleEntryAmountValue: number;
  maxWinning: number;
  disabled: boolean;
  minLimitError: boolean;
  minLimitValue: number;
  hasEnoughMoney: boolean;
  amount: RecordOfStakeTypeAmountType;
  modifier: AmountInputModifier;
};

const Single: FC<StoreProps & DispatchProp> = ({
  betsSize,
  totalAmountActive,
  singleEntryAmountValue,
  maxWinning,
  disabled,
  minLimitError,
  minLimitValue,
  hasEnoughMoney,
  amount,
  modifier,
  dispatch,
}) => {
  const { t } = useTranslation();
  const isAllowTotalAmount = useFeatureFlag(FeatureFlag.ALLOW_TOTAL_AMOUNT);
  const onChangeCheckboxHandler = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const modifier = event.target.checked
      ? AmountInputModifier.MULTIPLY
      : AmountInputModifier.DIVIDE;

    dispatch(changeStakeTypeAmount(amount.value, amount.account, modifier));
  }, [amount]);

  const _textBarClassName = classNames({
    [styles.disabledTextBar]: disabled,
  });

  const renderFooterContent = () => {
    if (!disabled) {
      if (!hasEnoughMoney) {
        return (
          <NoMoneyLink />
        );
      }

      if (minLimitError) {
        return (
          <>
            <TextWithAmountBar value={maxWinning}
                               data-test="stakeTypeSingle-maxWinning"
                               label={dictionary.maxWinningLabel}
                               className={_textBarClassName} />
            <TextWithAmountBar hasError
                               data-test="stakeTypeSingle-minLimitError"
                               value={minLimitValue}
                               label={dictionary.minLimitLabel} />
            <MinLimitError value={minLimitValue * betsSize}
                           label={dictionary.minLimitErrorLabel} />
          </>
        );
      }
    }

    if (amount.value) {
      return (
        <>
          <TextWithAmountBar value={singleEntryAmountValue}
                             label={dictionary.singleEntryAmountLabel}
                             data-test="stakeTypeSingle-singleEntryAmount"
                             className={_textBarClassName} />
          <TextWithAmountBar value={maxWinning}
                             data-test="stakeTypeSingle-maxWinning"
                             label={dictionary.maxWinningLabel}
                             className={_textBarClassName} />
        </>
      );
    }
    const PresetsAndFreebetsBar = getPresetsAndFreebetsBar('single');
    return (
      <PresetsAndFreebetsBar />
    );
  };

  return (
    <div className={styles.container}
         data-test="stakeTypeSingle-container">
      {isAllowTotalAmount && <TotalAmountInputToggle className={styles.toggle} />}

      {totalAmountActive && (
        <>
          <AmountInput hasError={!disabled && (!hasEnoughMoney || minLimitError)} />

          {renderFooterContent()}

          {modifier && (
            <Checkbox checked={modifier === AmountInputModifier.MULTIPLY}
                      disabled={disabled}
                      data-test="stakeTypeSingle-checkbox"
                      name="single_stake_type_same_amount"
                      label={t(dictionary.checkboxLabel)}
                      data-ga-event="cart-bidUnitedEvenSumToggle"
                      onChange={onChangeCheckboxHandler}
                      className={classNames(styles.checkbox, {
                        [styles.disabledCheckbox]: disabled,
                      })} />
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const betsSize = selectSingleBetsInfo(state).size;

  const totalAmountActive = selectTotalAmountActive(state);
  const validation = selectSingleAmountsValidation(state);

  return {
    betsSize,
    totalAmountActive,
    minLimitValue: selectSingleEntryIdsWithTheLargestMinLimit(state).limit,
    minLimitError: validation === AmountValidationResult.MIN_NOT_REACHED,
    singleEntryAmountValue: Number(selectSingleAmountEntry(state).value),
    maxWinning: selectSinglesMaxWinning(state),
    disabled: selectSubmission(state) !== SubmissionState.AWAITING,
    hasEnoughMoney: selectHasEnoughMoney(state),
    amount: selectAmountStakeType(state),
    modifier: selectSingleAmountModifier(state),
  };
};

export default connect(mapStateToProps)(Single);
